import { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { QuickStartGuideItemIDs } from './QuickStartGuideItems.constants';

// Selectors
import { getUserFlag } from 'Shared/selectors/user';

// Utils
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';
import { trackQuickStartGuideItemStatus } from 'Shared/tracking/tracking';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

function useUpdateQuickStartGuideFlag() {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();

  const quickStartGuideData = useSelector(state => getUserFlag(state, { flag: 'quick_start_guide_data' })) || {
    steps: [],
  };

  const handleSaveData = useCallback(
    data => {
      dispatch(UsersActions.updateUserFlag('quick_start_guide_data', { ...quickStartGuideData, ...data }));
    },
    [quickStartGuideData]
  );

  const hasBeenSet = useCallback(
    (id: QuickStartGuideItemIDs) => {
      if (quickStartGuideData.steps[id]) return true;
      return false;
    },
    [quickStartGuideData]
  );

  const handleCompleteItem = useCallback(
    (id: QuickStartGuideItemIDs) => {
      if (!hasBeenSet(id)) {
        handleSaveData({ steps: { ...quickStartGuideData.steps, [id]: 'complete' } });
        trackQuickStartGuideItemStatus(id, 'complete', isOpen);
      }
    },
    [quickStartGuideData, handleSaveData, isOpen, hasBeenSet]
  );

  const handleSkipItem = useCallback(
    (id: QuickStartGuideItemIDs) => {
      if (!hasBeenSet(id)) {
        handleSaveData({ steps: { ...quickStartGuideData.steps, [id]: 'skipped' } });
        trackQuickStartGuideItemStatus(id, 'skipped', isOpen);
      }
    },
    [quickStartGuideData, handleSaveData, hasBeenSet, isOpen]
  );

  const openQuickStartGuide = useCallback(() => {
    const currentTimestamp = convertMomentToUnix(getCurrentMoment());
    handleSaveData({ lastOpenTime: currentTimestamp, isOpen: true });
    setIsOpen(true);
  }, [handleSaveData]);

  const closeQuickStartGuide = useCallback(() => {
    handleSaveData({ isOpen: false });
    setIsOpen(false);
  }, [handleSaveData]);

  const updateIsComplete = useCallback(() => {
    setIsOpen(false);
    const currentTimestamp = convertMomentToUnix(getCurrentMoment());
    handleSaveData({ isOpen: false, completedAt: currentTimestamp });
  }, [handleSaveData]);

  const dismissGuide = useCallback(() => {
    setIsOpen(false);
    const currentTimestamp = convertMomentToUnix(getCurrentMoment());
    handleSaveData({ dismissed: currentTimestamp });
  }, [handleSaveData]);

  // useEffect(() => {
  //   const now = getCurrentMoment();
  //   const isOnboardingCompletedLessThanThirtySeconds = now.diff(moment.unix(onboardingCompleteTime), 'seconds') < 12;
  //   if (isOnboardingCompletedLessThanThirtySeconds) {
  //     openQuickStartGuide();
  //   }
  // }, []);

  useEffect(() => {
    if (Boolean(quickStartGuideData?.completedAt) === false) {
      if (quickStartGuideData?.isOpen) {
        openQuickStartGuide();
      } else {
        // (josh) - this will pop the the quick start guide at least once every 24 hours
        // but not after 7 days of onboarding.
        // this does require a page "refresh" - aka - it doesn't just pop up automatically
        // const now = getCurrentMoment();
        // const isOnboardingCompletedLessThan7Days = now.diff(moment.unix(onboardingCompleteTime), 'days') < 7;
        // const isLastOpenMoreThan24HoursAgo = now.diff(moment.unix(quickStartGuideData?.lastOpenTime), 'hours') > 24;
        // if (isOnboardingCompletedLessThan7Days && isLastOpenMoreThan24HoursAgo) {
        //   openQuickStartGuide();
        // }
      }
    }
  }, []);

  return {
    isOpen,
    isDismissed: Boolean(quickStartGuideData?.dismissed),
    isComplete: Boolean(quickStartGuideData?.completedAt),
    quickStartGuideData: quickStartGuideData.steps,
    handleCompleteItem,
    handleSkipItem,
    hasBeenSet,
    openQuickStartGuide,
    closeQuickStartGuide,
    updateIsComplete,
    dismissGuide,
  };
}

export default useUpdateQuickStartGuideFlag;
